import { useState } from 'react'
import {
  ArrowRight,
  FileWarningIcon,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ProductSource } from '@/shared/hooks/api/productSource'
import {
  Attachment,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from '@/components/ui/attachment'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useIngestionDisplayPercent } from '@/features/product-catalog/hooks/useIngestionDisplayPercent'
import { resolveIngestionFailureText } from '@/features/product-catalog/utils/ingestionFailureMessage'
import { getProductSourceIcon } from '@/features/product-catalog/utils/getProductSourceIcon'
import { isRagIngestedProductSource } from '../utils/isRagIngestedProductSource'
import {
  isProductSourceFailed,
  isProductSourceInFlight,
} from '../utils/productChatAvailability'
import {
  isProductSourceUploadIncomplete,
} from '../utils/productSourceUploadStatus'
import { formatClassificationDisplayValue } from '../utils/productSourceClassificationDisplay'
import { toMachineStageKey } from '../utils/toMachineStageKey'
import { ProductSourceClassificationSheet } from './ProductSourceClassificationSheet'

type AttachmentState = 'idle' | 'uploading' | 'processing' | 'error' | 'done'

function resolveAttachmentState(
  source: ProductSource,
  isUploading: boolean
): AttachmentState {
  if (isUploading) {
    return 'uploading'
  }
  if (isProductSourceFailed(source)) {
    return 'error'
  }
  if (isProductSourceInFlight(source)) {
    return 'processing'
  }
  if (source.ingestionStatus === 'COMPLETED') {
    return 'done'
  }
  return 'idle'
}

export function ProductSourceListItem({
  productId,
  source,
  isUploading = false,
  deleteDisabled = false,
  onDelete,
  inspectPath,
}: {
  productId: string
  source: ProductSource
  isUploading?: boolean
  deleteDisabled?: boolean
  onDelete: () => Promise<void>
  inspectPath?: string
}) {
  const { t } = useTranslation('productCatalog')
  const [sheetOpen, setSheetOpen] = useState(false)
  const showClassificationSummary = isRagIngestedProductSource(source)

  const classificationDisplay = formatClassificationDisplayValue(
    source.classification
  )
  const hasClassification = classificationDisplay.length > 0
  const classificationSummary = hasClassification
    ? classificationDisplay
    : t('sources.classification.notSet')

  const isIngestionInProgress = isUploading || isProductSourceInFlight(source)
  const isSourceClickable = !isIngestionInProgress
  const attachmentState = resolveAttachmentState(source, isUploading)
  const shownPercent = useIngestionDisplayPercent({
    sourceId: source.id,
    status: source.ingestionStatus,
    fileUploadStatus: source.fileUploadStatus,
    filePathInBucket: source.filePathInBucket,
    isUploading,
    progress: source.ingestionProgress,
  })

  const SourceIcon = getProductSourceIcon(source.type)
  const translateIngestionFailure = (key: string, defaultValue: string) =>
    t(`sources.${key}`, { defaultValue })
  const failureMessage = resolveIngestionFailureText({
    ingestionError: source.ingestionError,
    ingestionErrorCode: source.ingestionErrorCode,
    fileUploadStatus: source.fileUploadStatus,
    sourceContentType: source.type,
    sourceFileName: source.name,
    translate: translateIngestionFailure,
  })

  let description = t('sources.status.readyToUpload')
  if (attachmentState === 'uploading') {
    description = t('sources.upload.uploading')
  } else if (isProductSourceUploadIncomplete(source)) {
    description = t('sources.status.uploadIncomplete')
  } else if (attachmentState === 'processing') {
    const machineStageKey = toMachineStageKey(source.ingestionStage)
    const stageLabel =
      machineStageKey != null
        ? t(
            `sources.status.stages.${machineStageKey}`,
            t('sources.status.processing')
          )
        : t('sources.status.processing')
    if (shownPercent != null) {
      description = `${stageLabel}… ${shownPercent}%`
    } else {
      description = `${stageLabel}…`
    }
  } else if (attachmentState === 'error') {
    description = failureMessage
  } else if (attachmentState === 'done') {
    if (showClassificationSummary) {
      description = classificationSummary
    } else {
      description = t('sources.status.ingestionCompleted')
    }
  }

  const isLoading =
    attachmentState === 'uploading' || attachmentState === 'processing'
  const isError = attachmentState === 'error'

  return (
    <>
      <Attachment state={attachmentState} size='sm' className='w-full'>
        <AttachmentMedia>
          {isLoading ? (
            <Loader2
              data-slot='spinner'
              className='animate-spin'
              aria-hidden
            />
          ) : isError ? (
            <FileWarningIcon aria-hidden />
          ) : (
            <SourceIcon />
          )}
        </AttachmentMedia>

        <AttachmentContent>
          <AttachmentTitle title={source.name}>
            {source.name}
          </AttachmentTitle>
          <AttachmentDescription title={isError ? failureMessage : description}>
            {isError ? failureMessage : description}
          </AttachmentDescription>
        </AttachmentContent>

        {isSourceClickable ? (
          <AttachmentActions>
            <ArrowRight className='ml-1 size-4' aria-hidden />
          </AttachmentActions>
        ) : null}

        {isSourceClickable && isError && (
          <Tooltip>
            <TooltipTrigger asChild>
              <AttachmentTrigger asChild>
                <button
                  type='button'
                  onClick={() => setSheetOpen(true)}
                  aria-label={t('sources.classification.openSheetAria', {
                    name: source.name,
                  })}
                />
              </AttachmentTrigger>
            </TooltipTrigger>
            <TooltipContent className='max-w-md text-left whitespace-pre-wrap break-words'>
              {failureMessage}
            </TooltipContent>
          </Tooltip>
        )}

        {isSourceClickable && !isError && (
          <AttachmentTrigger asChild>
            <button
              type='button'
              onClick={() => setSheetOpen(true)}
              aria-label={t('sources.classification.openSheetAria', {
                name: source.name,
              })}
            />
          </AttachmentTrigger>
        )}
      </Attachment>

      <ProductSourceClassificationSheet
        productId={productId}
        source={source}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        isUploading={isUploading}
        deleteDisabled={deleteDisabled}
        onDelete={onDelete}
        inspectPath={inspectPath}
      />
    </>
  )
}
